// Inertia 3 fires `httpException` for every non-Inertia response (any status,
// except its own 409 redirect/location responses) and — unlike v2's `invalid`,
// which this replaces — also for an Inertia page with a status >= 400, which it
// still renders unless the event is cancelled. Left alone, a non-Inertia
// response is shown verbatim in Inertia's full-screen error dialog —
// rack-attack's JSON 429, the static 5xx/422/404 pages, kamal's 503
// maintenance page.
//
// An expired session is NOT one of them: the XHR follows the redirect to the
// login page, which comes back as an ordinary Inertia page. A login page only
// reaches this handler if it arrives as plain HTML, and a bare 401/403 is
// treated the same way.
export const RATE_LIMITED = 'rate_limited';
export const AUTH = 'auth';
export const SERVER_ERROR = 'server_error';
export const UNKNOWN = 'unknown';

// The login page's Inertia component as it appears in an HTML body: page JSON
// in a <script data-page> element ("component":"auth/login", optionally with an
// escaped slash) or HTML-escaped in a data-page attribute
// (&quot;component&quot;:&quot;auth/login&quot;). Matching the component name —
// not a URL — is what makes this work across locales: route_translator serves
// the page under per-locale slugs (/login, /cs/prihlaseni, /de/anmelden, …).
const LOGIN_COMPONENT_MARKER =
  /(?:"|&quot;)component(?:"|&quot;)\s*:\s*(?:"|&quot;)auth\\?\/login(?:"|&quot;)/;

// inertia_rails renders the page JSON into this element
// (config/initializers/inertia.rb: use_script_element_for_initial_page).
const PAGE_SCRIPT =
  /<script\b[^>]*\bdata-page="app"[^>]*>([\s\S]*?)<\/script>/i;

const LOGIN_PATH = '/login';

export function isInertiaResponse(response) {
  return response?.headers?.['x-inertia'] !== undefined;
}

export function isLoginPageResponse(response) {
  const data = response?.data;
  if (typeof data === 'string') return LOGIN_COMPONENT_MARKER.test(data);
  return data?.component === 'auth/login';
}

// `response` is Inertia 3's HttpExceptionResponse: { status, data, headers },
// with `data` JSON-parsed when possible and lower-cased header names.
export function classifyHttpException(response) {
  const status = response?.status;
  if (status === 429) return RATE_LIMITED;
  if (status >= 500) return SERVER_ERROR;
  if (status === 401 || status === 403) return AUTH;
  if (isLoginPageResponse(response)) return AUTH;
  return UNKNOWN;
}

function pageFrom(data) {
  if (typeof data !== 'string') return data;
  const json = data.match(PAGE_SCRIPT)?.[1];
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Inertia 3 doesn't expose the post-redirect URL, so take the login page's own
// URL from its page JSON — it carries the user's locale (/de/anmelden). Only a
// same-origin URL is followed, and it is returned absolute: a normalized path
// such as `/.//evil.example` would otherwise read as protocol-relative.
export function loginRedirectUrl(response) {
  if (!isLoginPageResponse(response)) return LOGIN_PATH;
  const url = pageFrom(response.data)?.url;
  if (typeof url !== 'string') return LOGIN_PATH;
  try {
    const target = new URL(url, window.location.origin);
    return target.origin === window.location.origin ? target.href : LOGIN_PATH;
  } catch {
    return LOGIN_PATH;
  }
}

const NOTICES = {
  [RATE_LIMITED]: 'errors.rateLimited',
  [SERVER_ERROR]: 'errors.serverError',
  [UNKNOWN]: 'common.error',
};

// Expired session → send the user to login. Everything else keeps the user
// on the page with a toast — bouncing to /login loses their place, and a
// rate-limited redirect would only issue another throttled request.
export function installInertiaHttpExceptionHandler(
  router,
  { notify, redirect, addBreadcrumb },
) {
  return router.on('httpException', (event) => {
    const response = event.detail.response;
    // An Inertia page with an error status is the server's chosen error
    // screen; cancelling the event would skip rendering it.
    if (isInertiaResponse(response)) return;

    event.preventDefault();
    addBreadcrumb({
      category: 'inertia',
      message: 'Non-Inertia response',
      level: 'warning',
      data: {
        status: response?.status,
        contentType: response?.headers?.['content-type'],
      },
    });
    const kind = classifyHttpException(response);
    if (kind === AUTH) {
      redirect(loginRedirectUrl(response));
    } else {
      notify(NOTICES[kind]);
    }
  });
}
